import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../FishFinderBuilder.css';
import './CompareScreensV2.css';
import { fetchCompareScreensProducts } from '../../utils/supabaseCompareScreens';
import {
  getCompareV2Model,
  getAllowedModelIdsForSizeAnswer,
  normalizeChooseSizeKey,
  modelMatchesChooseSizeAnswer,
  COMPARE_V2_RECOMMENDED_QUERY,
} from '../../data/compareScreensV2Models';
import { loadChooseAnswers, persistChooseAnswers } from '../../utils/compareScreensV2Session';

const Q1 = {
  id: 'where',
  title: 'Where will you use it most?',
  options: [
    { value: 'ice', label: 'Ice fishing' },
    { value: 'open', label: 'Open water' },
    { value: 'both', label: 'Both' },
  ],
};

const Q2 = {
  id: 'priority',
  title: 'What matters most to you?',
  options: [
    { value: 'money', label: 'Saving money' },
    { value: 'bigger', label: 'Bigger screen' },
    {
      value: 'balance',
      label: 'Smart middle ground (price + screen + features)',
    },
    { value: 'premium', label: 'Premium setup' },
  ],
};

const Q3 = {
  id: 'size',
  title: 'What screen size sounds best?',
  options: [
    { value: '7', label: '7 inch' },
    { value: '9', label: '9 inch' },
    { value: '10', label: '10 inch' },
    { value: '12', label: '12 inch+' },
  ],
};

function modelLabel(id) {
  return getCompareV2Model(id)?.shortLabel ?? id;
}

/**
 * Scores only models in `allowed`. Mutates scores ONLY for those ids so sort never sees NaN
 * (previously, += on undefined produced NaN and could scramble ordering).
 */
function scoreModels(where, priority, size) {
  const sizeNorm = normalizeChooseSizeKey(size);
  const allowed = getAllowedModelIdsForSizeAnswer(sizeNorm);
  const scores = Object.fromEntries(allowed.map((id) => [id, 0]));

  if (allowed.length === 0) {
    return { bestId: null, alternateId: null, scores: {} };
  }

  const add = (id, delta) => {
    if (Object.prototype.hasOwnProperty.call(scores, id)) scores[id] += delta;
  };

  if (where === 'ice') {
    add('echomap-73sv-uhd2', 5);
    add('echomap-93sv-uhd2', 4);
    add('gpsmap-943xsv', 1);
    add('gpsmap-1042xsv', 0);
    add('gpsmap-1243xsv', 0);
  } else if (where === 'open') {
    add('echomap-73sv-uhd2', 1);
    add('echomap-93sv-uhd2', 3);
    add('gpsmap-943xsv', 3);
    add('gpsmap-1042xsv', 4);
    add('gpsmap-1243xsv', 5);
  } else {
    add('echomap-73sv-uhd2', 2);
    add('echomap-93sv-uhd2', 5);
    add('gpsmap-943xsv', 4);
    add('gpsmap-1042xsv', 3);
    add('gpsmap-1243xsv', 2);
  }

  if (priority === 'money') {
    add('echomap-73sv-uhd2', 6);
    add('echomap-93sv-uhd2', 4);
    add('gpsmap-943xsv', 2);
    add('gpsmap-1042xsv', 1);
    add('gpsmap-1243xsv', 0);
  } else if (priority === 'bigger') {
    add('echomap-73sv-uhd2', 0);
    add('echomap-93sv-uhd2', 3);
    add('gpsmap-943xsv', 3);
    add('gpsmap-1042xsv', 5);
    add('gpsmap-1243xsv', 6);
  } else if (priority === 'balance') {
    add('echomap-73sv-uhd2', 2);
    add('echomap-93sv-uhd2', 6);
    add('gpsmap-943xsv', 5);
    add('gpsmap-1042xsv', 3);
    add('gpsmap-1243xsv', 2);
  } else if (priority === 'premium') {
    add('echomap-73sv-uhd2', 0);
    add('echomap-93sv-uhd2', 2);
    add('gpsmap-943xsv', 3);
    add('gpsmap-1042xsv', 5);
    add('gpsmap-1243xsv', 6);
  }

  const ranked = allowed.slice().sort((a, b) => {
    const d = scores[b] - scores[a];
    if (d !== 0) return d;
    return allowed.indexOf(a) - allowed.indexOf(b);
  });

  let bestId = ranked[0];
  const bestModel = getCompareV2Model(bestId);
  if (!bestModel || !modelMatchesChooseSizeAnswer(bestModel, sizeNorm)) {
    bestId = allowed[0];
  }

  const alternateId = ranked.find((id) => id !== bestId) ?? null;

  return {
    bestId,
    alternateId,
    scores,
  };
}

function buildBullets(bestId, where, priority, size) {
  const model = getCompareV2Model(bestId);
  const name = model?.shortLabel ?? modelLabel(bestId);
  const sizeNorm = normalizeChooseSizeKey(size);
  const sizeLabel = Q3.options.find((o) => o.value === sizeNorm)?.label ?? 'that size';

  const lines = [];
  if (model) {
    lines.push(
      `Top pick: ${name} (${model.screenSizeLabel}), kept in your ${sizeLabel} size class.`,
    );
  } else {
    lines.push(`Top pick: ${name}, aligned with your ${sizeLabel} size choice.`);
  }
  lines.push(
    `Fits ${where === 'ice' ? 'ice' : where === 'open' ? 'open water' : 'mixed ice and open-water'} use based on your first answer.`,
  );

  if (priority === 'money') {
    lines.push(`Prioritizes value within that size so more budget stays for transducers and rigging.`);
  } else if (priority === 'bigger') {
    lines.push(
      `Within that size class, favors the strongest match for split views and readability.`,
    );
  } else if (priority === 'balance') {
    lines.push(
      `Balances price, screen area, and features: the “no regrets” pick for most builds.`,
    );
  } else {
    lines.push(`Lines up with a premium electronics plan and flagship-level expectations.`);
  }

  const core = lines.slice(0, 3);
  core.push(`Tap below to open compare with ${name} pre-selected.`);
  return core.slice(0, 4);
}

function alternateBlurb(altId, bestId, size) {
  if (!altId || altId === bestId) return '';
  const alt = getCompareV2Model(altId);
  const best = getCompareV2Model(bestId);
  if (!alt || !best) return '';
  const sizeNorm = normalizeChooseSizeKey(size);
  const sizeLabel = Q3.options.find((o) => o.value === sizeNorm)?.label ?? 'that size';

  if (alt.screenSizeInches === best.screenSizeInches) {
    return `Runner-up: ${alt.shortLabel} (${alt.screenSizeLabel}). Same ${sizeLabel} size as ${best.shortLabel}, different lineup (ECHOMAP vs GPSMAP). Add both in compare if you’re deciding between them.`;
  }
  return `Alternate: ${alt.shortLabel} (${alt.screenSizeLabel}). Different physical size than ${best.shortLabel}; only compare if you’re open to moving away from ${sizeLabel}.`;
}

function CompareScreensV2Choose() {
  const persisted = loadChooseAnswers();
  const [where, setWhere] = useState(persisted?.where ?? null);
  const [priority, setPriority] = useState(persisted?.priority ?? null);
  const [size, setSize] = useState(persisted?.size ?? null);
  const [compareScreenCatalog, setCompareScreenCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchCompareScreensProducts()
      .then((rows) => {
        if (!cancelled) setCompareScreenCatalog(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCompareScreenCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (where && priority && size) {
      persistChooseAnswers({ where, priority, size });
    }
  }, [where, priority, size]);

  const complete = Boolean(where && priority && size);

  const result = useMemo(() => {
    if (!where || !priority || !size) return null;
    const { bestId, alternateId } = scoreModels(where, priority, size);
    return {
      bestId,
      alternateId,
      bullets: bestId ? buildBullets(bestId, where, priority, size) : [],
      alternateNote:
        bestId && alternateId ? alternateBlurb(alternateId, bestId, size) : '',
    };
  }, [where, priority, size]);

  const recommendedImageUrl = useMemo(() => {
    if (!result?.bestId || !compareScreenCatalog.length) return null;
    const row = compareScreenCatalog.find((p) => p.slug === result.bestId);
    return row?.image_url ?? null;
  }, [result?.bestId, compareScreenCatalog]);

  return (
    <div className="compare-v2-page compare-v2-choose-page">
      <header className="compare-v2-hero">
        <h1>Find the Right Garmin Screen</h1>
        <p className="compare-v2-lede">
          Answer a few quick questions and get a recommendation
        </p>
      </header>

      <div className="compare-v2-choose-flow">
        <section className="compare-v2-choose-block" aria-labelledby="choose-q1">
          <h2 id="choose-q1" className="compare-v2-choose-q-title">
            <span className="compare-v2-choose-step">1</span>
            {Q1.title}
          </h2>
          <div className="compare-v2-choose-options" role="radiogroup" aria-label={Q1.title}>
            {Q1.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={where === opt.value}
                className={`compare-v2-choose-opt${where === opt.value ? ' selected' : ''}`}
                onClick={() => setWhere(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <section className="compare-v2-choose-block" aria-labelledby="choose-q2">
          <h2 id="choose-q2" className="compare-v2-choose-q-title">
            <span className="compare-v2-choose-step">2</span>
            {Q2.title}
          </h2>
          <div className="compare-v2-choose-options" role="radiogroup" aria-label={Q2.title}>
            {Q2.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={priority === opt.value}
                className={`compare-v2-choose-opt${priority === opt.value ? ' selected' : ''}`}
                onClick={() => setPriority(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <section className="compare-v2-choose-block" aria-labelledby="choose-q3">
          <h2 id="choose-q3" className="compare-v2-choose-q-title">
            <span className="compare-v2-choose-step">3</span>
            {Q3.title}
          </h2>
          <div className="compare-v2-choose-options" role="radiogroup" aria-label={Q3.title}>
            {Q3.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={size === opt.value}
                className={`compare-v2-choose-opt${size === opt.value ? ' selected' : ''}`}
                onClick={() => setSize(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>
      </div>

      {complete && result && result.bestId && (
        <section className="compare-v2-choose-result" aria-labelledby="choose-result-heading">
          <div className="compare-v2-choose-result-head">
            {recommendedImageUrl && (
              <img
                className="compare-v2-choose-result-img"
                src={recommendedImageUrl}
                alt=""
              />
            )}
            <div className="compare-v2-choose-result-head-text">
              <h2 id="choose-result-heading" className="compare-v2-choose-result-title">
                Best Pick for You
              </h2>
              <p className="compare-v2-choose-result-model">{modelLabel(result.bestId)}</p>
            </div>
          </div>
          <ul className="compare-v2-choose-bullets">
            {result.bullets.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
          {result.alternateNote && (
            <p className="compare-v2-choose-alternate">{result.alternateNote}</p>
          )}
          <div className="compare-v2-choose-cta">
            <Link
              to={`/compare-screens-v2/compare?${COMPARE_V2_RECOMMENDED_QUERY}=${encodeURIComponent(result.bestId)}`}
              className="primary-cta-button compare-v2-choose-cta-btn"
            >
              Compare This Screen
            </Link>
            <p className="compare-v2-choose-cta-note">
              Opens compare with this model selected. Add one or two others for a full side-by-side.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

export default CompareScreensV2Choose;
